import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import OpenAppMenu from "./OpenAppMenu.jsx";

const desktopSignOutClassName =
  "inline-flex items-center gap-1.5 rounded-[10px] px-2 py-1.5 text-xs font-medium text-hc-text-muted hover:bg-white/5 transition";

const mobileSignOutClassName =
  "flex w-full items-center gap-2.5 rounded-[12px] px-3 py-2.5 text-sm font-medium text-hc-text-dim transition hover:bg-white/5 hover:text-hc-text";

function SignOutButton({ onClick, className, iconClassName = "leading-none opacity-80" }) {
  return (
    <button type="button" onClick={onClick} className={className}>
      <i className={`icon-logout-right ${iconClassName}`} aria-hidden="true" />
      Sign out
    </button>
  );
}

export default function AppHeader({ navItems, onSignOut, homeHref = "/dashboard" }) {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks = navItems?.map((item) => {
    const active =
      item.to === "/programs"
        ? pathname === item.to || pathname.startsWith("/programs/")
        : pathname === item.to;
    return { ...item, active };
  });

  return (
    <header className="sticky top-0 z-20 overflow-visible border-b border-hc-surface-border bg-hc-surface backdrop-blur-sm">
      <div className="mx-auto flex max-w-6xl items-center gap-2 pr-6 py-3">
        <Link to={homeHref} className="app-header-logo-wrap shrink-0">
          <img src="/logo_full.png" alt="Hey Coach AI logo" />
        </Link>

        {navLinks && (
          <nav className="hidden min-[910px]:flex flex-1 items-center gap-0.5 px-4" aria-label="Main">
            {navLinks.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className={`flex shrink-0 items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-sm font-medium transition ${item.active
                  ? "bg-hc-primary/10 text-hc-primary"
                  : "text-hc-text-dim hover:bg-white/5 hover:text-hc-text"
                  }`}
              >
                <i className={`${item.icon} text-xs leading-none`} aria-hidden="true" />
                {item.label}
              </Link>
            ))}
          </nav>
        )}

        <div className={navLinks ? "flex-1 min-[910px]:hidden" : "flex-1"} />

        <div className="flex shrink-0 items-center gap-2">
          <div className="relative hidden min-[910px]:flex items-center gap-2">
            <OpenAppMenu />
            {onSignOut && (
              <SignOutButton onClick={onSignOut} className={desktopSignOutClassName} />
            )}
          </div>

          {navLinks && (
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              className="flex items-center justify-center rounded-[10px] p-2 text-hc-text-muted hover:bg-white/5 min-[910px]:hidden"
            >
              <i
                className={`${menuOpen ? "icon-x" : "icon-menu"} text-base leading-none`}
                aria-hidden="true"
              />
            </button>
          )}

          {!navLinks && (
            <div className="flex min-[910px]:hidden items-center gap-2">
              <OpenAppMenu />
              {onSignOut && (
                <SignOutButton onClick={onSignOut} className={desktopSignOutClassName} />
              )}
            </div>
          )}
        </div>
      </div>

      {navLinks && menuOpen && (
        <div className="border-t border-hc-surface-border px-4 pb-3 pt-2 min-[910px]:hidden">
          <nav className="flex flex-col gap-0.5" aria-label="Main mobile">
            {navLinks.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-2.5 rounded-[12px] px-3 py-2.5 text-sm font-medium transition ${item.active
                  ? "bg-hc-primary/10 text-hc-primary"
                  : "text-hc-text-dim hover:bg-white/5 hover:text-hc-text"
                  }`}
              >
                <i className={`${item.icon} leading-none`} aria-hidden="true" />
                {item.label}
              </Link>
            ))}

            <div className="mt-1 flex flex-col gap-0.5 border-t border-hc-surface-border pt-2">
              <OpenAppMenu layout="menu" onNavigate={() => setMenuOpen(false)} />
            </div>

            {onSignOut && (
              <div className="mt-1 border-t border-hc-surface-border pt-2">
                <SignOutButton
                  onClick={() => {
                    setMenuOpen(false);
                    onSignOut();
                  }}
                  className={mobileSignOutClassName}
                  iconClassName="leading-none"
                />
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
